import React, { useEffect, useState, useRef } from 'react';
import { useParams, NavLink, Outlet } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Info, Users, Building2, CreditCard, MapPin, Loader2, History } from 'lucide-react';
import { getDistrictById, getRegionById } from '@/data/territoryRepository';
import type { District } from '@/models/district';
import type { Region } from '@/models/region';

interface TabItemProps {
  to: string;
  icon: React.ElementType;
  children: React.ReactNode;
  color?: 'primary' | 'accent' | 'secondary' | 'success' | 'info';
}

function TabItem({ to, icon: Icon, children, color = 'primary' }: TabItemProps) {
  const colorClasses = {
    primary: {
      border: 'border-primary',
      text: 'text-primary',
      hover: 'hover:text-primary hover:bg-primary/10 hover:border-primary/50'
    },
    accent: {
      border: 'border-accent',
      text: 'text-accent',
      hover: 'hover:text-accent hover:bg-accent/10 hover:border-accent/50'
    },
    secondary: {
      border: 'border-secondary',
      text: 'text-secondary',
      hover: 'hover:text-secondary hover:bg-secondary/10 hover:border-secondary/50'
    },
    success: {
      border: 'border-success',
      text: 'text-success',
      hover: 'hover:text-success hover:bg-success/10 hover:border-success/50'
    },
    info: {
      border: 'border-info',
      text: 'text-info',
      hover: 'hover:text-info hover:bg-info/10 hover:border-info/50'
    }
  };
  
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
          colorClasses[color].hover,
          isActive
            ? `${colorClasses[color].border} ${colorClasses[color].text}`
            : 'border-transparent text-muted-foreground'
        )
      }
    >
      <Icon className="size-4" />
      <span>{children}</span>
    </NavLink>
  );
}

export default function TerritoryLayout() {
  const { districtId, regionId } = useParams<{ districtId?: string; regionId?: string }>();
  const [district, setDistrict] = useState<District | null>(null);
  const [region, setRegion] = useState<Region | null>(null);
  const [loading, setLoading] = useState(true);
  const lastLoadedIdRef = useRef<string | null>(null);
  
  const isDistrict = !!districtId;
  const territoryId = districtId || regionId;
  const baseUrl = isDistrict ? `/territories/districts/${districtId}` : `/territories/regions/${regionId}`;
  
  useEffect(() => {
    const fetchTerritory = async () => {
      if (!territoryId) return;
      
      // Prevent duplicate loads for same territory
      const loadKey = `${isDistrict ? 'D' : 'R'}-${territoryId}`;
      if (lastLoadedIdRef.current === loadKey) return;
      lastLoadedIdRef.current = loadKey;
      
      setLoading(true);
      try {
        if (isDistrict) {
          const data = await getDistrictById(territoryId);
          setDistrict(data);
          setRegion(null);
        } else {
          const data = await getRegionById(territoryId);
          setRegion(data);
          setDistrict(null);
        }
      } catch (error) {
        console.error('Error fetching territory:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTerritory();
  }, [territoryId, isDistrict]);
  
  const refreshTerritory = async () => {
    if (!territoryId) return;
    
    try {
      if (isDistrict) {
        const data = await getDistrictById(territoryId);
        setDistrict(data);
      } else {
        const data = await getRegionById(territoryId);
        setRegion(data);
      }
    } catch (error) {
      console.error('Error refreshing territory:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  const territoryData = district || region;
  const territoryName = isDistrict
    ? `Distrito: ${district?.name || ''}`
    : `Región: ${region?.name || ''}`;
  
  if (!territoryData) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <MapPin className="mx-auto size-12 text-muted-foreground" />
          <h2 className="mt-4 text-lg font-semibold">Territorio no encontrado</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            El territorio solicitado no existe o ha sido eliminado.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-border bg-card px-6 py-4">
        <div className="flex items-center gap-3">
          <MapPin className="size-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">{territoryName}</h1>
            {region && region.district && (
              <p className="text-sm text-muted-foreground">
                {`Distrito: ${region.district.name}`}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="border-b border-border bg-card">
        <div className="flex gap-1 overflow-x-auto px-4">
          <TabItem to={`${baseUrl}/information`} icon={Info} color="primary">
            Información
          </TabItem>
          <TabItem to={`${baseUrl}/ministers`} icon={Users} color="accent">
            Ministros
          </TabItem>
          <TabItem to={`${baseUrl}/churches`} icon={Building2} color="info">
            Iglesias
          </TabItem>
          <TabItem to={`${baseUrl}/credentials`} icon={CreditCard} color="success">
            Credenciales
          </TabItem>
          <TabItem to={`${baseUrl}/historial`} icon={History} color="secondary">
            Historial
          </TabItem>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-auto bg-background">
        <Outlet context={{ district, region, isDistrict, refreshTerritory }} />
      </div>
    </div>
  );
}
